// BondSimulation.tsx - Main Component
import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Card, CardContent, Typography } from '@mui/material';
import { AccountBalance as BondIcon } from '@mui/icons-material';
import type { RootState, AppDispatch } from 'app/store';
import {
  runSimulation,
  SimulationPayload
} from 'slices/fundManagementInvestment/simulationDashboardSlice';

// Import modular components
import BondFormComponent from './BondForm';
import BondResults from './BondResults';
import {
  BondForm,
  BondExpandedSections,
  BondSectionKey,
  SimulationResult
} from '../simulationTypes';

const BondSimulation = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error, result } = useSelector((s: RootState) => s.simulationDashboard);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  const [expandedSections, setExpandedSections] = useState<BondExpandedSections>({
    basic: true,
    investment: false,
    interest: false,
    growth: false,
    risk: false
  });

  const [form, setForm] = useState<BondForm>({
    iterations: 10000,
    years: 10,
    assetId: 'bond_gov_001',
    assetName: 'Australian Government Bond Fund',
    purchasePrice: 250000,
    fees: 1250,
    setupCosts: 500,
    interestRateMin: 0.038,
    interestRateMax: 0.052,
    managementFeePercent: 0.008,
    transactionCostPercent: 0.002,
    capitalGrowthFixed: 0.01,
    volatilityStdDev: 0.04,
    recessionProb: 0.08,
    recessionImpact: 0.15,
    defaultProbability: 0.001,
    liquidityDiscount: 0.005
  });

  const handleInputChange = (field: keyof BondForm, value: number | string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleSection = (section: BondSectionKey) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Build API payload based on the provided structure - FIXED
  const buildPayload = (): SimulationPayload => ({
    simulation_config: {
      iterations: form.iterations,
      years: form.years,
      percentile_choices: ['P10', 'P50', 'P90']
    },
    assets: [
      {
        asset_id: form.assetId,
        asset_name: form.assetName,
        asset_type: 'bond' as const,
        initial_investment: {
          purchase_price: form.purchasePrice,
          upfront_costs: {
            fees: form.fees,
            setup_costs: form.setupCosts
          }
        },
        financing: {
          interest_rate: {
            type: 'range',
            min: form.interestRateMin,
            max: form.interestRateMax
          }
        },
        expenses: {
          variable_percent: {
            management_fee_percent: form.managementFeePercent,
            transaction_cost_percent: form.transactionCostPercent
          }
        },
        market_assumptions: {
          capital_growth: {
            type: 'fixed',
            fixed_value: form.capitalGrowthFixed  
          },
          volatility: {
            annual_std_deviation: form.volatilityStdDev
          },
          market_cycles: {
            recession_probability: form.recessionProb,
            recession_impact: form.recessionImpact
          }
        },
        risk_factors: {
          default_probability: form.defaultProbability,
          liquidity_discount: form.liquidityDiscount
        }
      }
    ]
  });

  const runBondSimulation = () => {
    const payload = buildPayload();
    dispatch(runSimulation(payload));
  };

  return (
    <Box sx={{ p: { xs: 0, sm: 1.5 } }}>
      <Grid container spacing={2}>
        {/* Form - 30% */}
        <Grid item xs={12} lg={3.5} sx={{ p: { xs: 0, lg: 2 } }}>
          <BondFormComponent
            form={form}
            handleInputChange={handleInputChange}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
            runBondSimulation={runBondSimulation}
            loading={loading}
            error={error}
          />
        </Grid>

        {/* Results - 70% */}
        <Grid item xs={12} lg={8.5}>
          {result ? (
            <BondResults
              result={result as SimulationResult}
              form={form}
              reportRef={reportRef as React.RefObject<HTMLDivElement>}
              exportMenuAnchor={exportMenuAnchor}
              setExportMenuAnchor={setExportMenuAnchor}
            />
          ) : (
            <Card
              elevation={1}
              sx={{
                height: '400px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CardContent sx={{ textAlign: 'center',p: { xs: 0.5, sm: 3 } }}>
                <BondIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Bond Investment Simulator Ready
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure bond parameters and run simulation for conservative fixed-income
                  analysis
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default BondSimulation;
